import { Component, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import {
    ConnectionStatus,
    ZitiConnectionState,
    ZitiMetrics,
    ZitiWasmService,
    overallConnectionStatus,
} from '../ziti-wasm.service';

const STATUS_TONE: Record<ConnectionStatus, string> = {
    connected: 'nf-badge--ok',
    connecting: 'nf-badge--warn',
    error: 'nf-badge--err',
    disconnected: 'nf-badge--muted',
};

const STATUS_LABEL: Record<ConnectionStatus, string> = {
    connected: 'Connected',
    connecting: 'Connecting',
    error: 'Error',
    disconnected: 'Offline',
};

const OVERALL_LABEL: Record<ConnectionStatus, string> = { ...STATUS_LABEL, error: 'Unavailable' };

const OVERALL_TEXT: Record<ConnectionStatus, string> = {
    connected: 'Management requests travel over the secure connection.',
    connecting: 'Connecting to the network controller over Ziti.',
    error: 'The secure connection is unavailable, so requests use a direct connection.',
    disconnected: 'The secure connection is not connected, so requests use a direct connection.',
};

const COPY_FEEDBACK_MS = 1500;

// Router addresses look like wss:host:port; placeholders such as "Discovering..." have no scheme
const ADDRESS_PATTERN = /^[a-z][a-z0-9+.-]*:/i;

interface RouterRow {
    url: string;
    tone: string;
    label: string;
    copyable: boolean;
}

@Component({
    selector: 'lib-ziti-metrics-panel',
    templateUrl: './ziti-metrics-panel.component.html',
    styleUrls: ['./ziti-metrics-panel.component.scss'],
    standalone: false,
})
export class ZitiMetricsPanelComponent implements OnDestroy {
    state: ZitiConnectionState = { controller: 'disconnected', controllerUrl: '', edgeRouters: [] };
    metrics: ZitiMetrics = {
        requestCount: 0,
        requestErrors: 0,
        totalBytesSent: 0,
        totalBytesReceived: 0,
        avgRequestDurationMs: 0,
        lastRequestDurationMs: 0,
        reconnects: 0,
    };
    overallTone = STATUS_TONE.disconnected;
    overallLabel = OVERALL_LABEL.disconnected;
    overallText = OVERALL_TEXT.disconnected;
    controllerTone = STATUS_TONE.disconnected;
    controllerLabel = STATUS_LABEL.disconnected;
    routers: RouterRow[] = [];
    bytesSent = '0 B';
    bytesReceived = '0 B';
    copied = '';
    canRetry = false;

    private copiedTimer: ReturnType<typeof setTimeout> | undefined;
    private subscription = new Subscription();

    constructor(private zitiWasmService: ZitiWasmService) {
        this.subscription.add(this.zitiWasmService.connectionState$.subscribe((s) => this.setState(s)));
        this.subscription.add(
            this.zitiWasmService.metrics$.subscribe((m) => {
                this.metrics = m;
                this.bytesSent = this.formatBytes(m.totalBytesSent);
                this.bytesReceived = this.formatBytes(m.totalBytesReceived);
            })
        );
    }

    copy(text: string) {
        if (!text) {
            return;
        }
        navigator.clipboard.writeText(text).then(() => {
            this.copied = text;
            clearTimeout(this.copiedTimer);
            this.copiedTimer = setTimeout(() => (this.copied = ''), COPY_FEEDBACK_MS);
        });
    }

    close() {
        this.zitiWasmService.metricsPanelOpen$.next(false);
    }

    retry() {
        this.zitiWasmService.retryRequested$.next();
    }

    ngOnDestroy() {
        clearTimeout(this.copiedTimer);
        this.subscription.unsubscribe();
    }

    private setState(state: ZitiConnectionState) {
        this.state = state;
        const overall = overallConnectionStatus(state);
        this.overallTone = STATUS_TONE[overall];
        this.overallLabel = OVERALL_LABEL[overall];
        this.overallText = OVERALL_TEXT[overall];
        this.canRetry = overall === 'error' || overall === 'disconnected';
        this.controllerTone = STATUS_TONE[state.controller];
        this.controllerLabel = STATUS_LABEL[state.controller];
        this.routers = state.edgeRouters.map((er) => ({
            url: er.url,
            tone: STATUS_TONE[er.status],
            label: STATUS_LABEL[er.status],
            copyable: ADDRESS_PATTERN.test(er.url),
        }));
    }

    private formatBytes(bytes: number): string {
        if (bytes < 1024) {
            return `${bytes} B`;
        }
        if (bytes < 1024 * 1024) {
            return `${(bytes / 1024).toFixed(1)} KB`;
        }
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
}
